import React, { useEffect, useRef, useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Container, Card, Button, Alert } from "react-bootstrap";
import { Spin } from "antd";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import PageTitle from "@/common/PageTitle/PageTitle";
import {
  useCanViewPage,
  useCanEditPage,
} from "@/contexts/SuperAdminIdentityContext";
import {
  useGetProcessingJobQuery,
  useListApprovedArtifactVersionsQuery,
  useReprocessProcessingJobMutation,
  useReplaceSongAudioMutation,
  useResumeProcessingJobMutation,
  useArchiveProcessingJobMutation,
  useRestoreProcessingJobMutation,
  isProcessingApiNotConfigured,
  extractProcessingErrorMessage,
  extractProcessingErrorReasonCode,
  extractReplacementAlreadyInProgress,
} from "@/features/processing/processingApi";
import { formatSongProcessingLoadFailure } from "@/features/processing/processingLoadError";
import {
  ARCHIVABLE_STATES,
  CHECKPOINT_LABELS,
  IN_FLIGHT_JOB_STATES,
  REPROCESSABLE_STATES,
  type ResumeJobRequest,
} from "@/features/processing/processingTypes";
import {
  ArchiveJobsModal,
  ConfidenceAuditModal,
  ReprocessConfirmModal,
  ResumeReviewModal,
  deriveParkReasonCode,
  jobHasConfidenceAudit,
} from "@/features/processing/lifecycleModals";
import {
  ReplaceSongAudioConfirmModal,
  type ReplaceSongAudioTarget,
} from "@/features/processing/ReplaceSongAudioConfirmModal";
import { initAndPutReplaceSongAudio } from "@/features/processing/replaceSongAudioClient";
import { processingJobPurposeLabel } from "@/features/processing/songPlayHistoryLabels";
import { validateMp3File } from "@/features/processing/processingUploadService";
import LineagePanel from "@/features/processing/LineagePanel";
import JobGuidancePanel from "@/features/processing/JobGuidancePanel";
import { EntityRefBadge } from "@/components/EntityRef/EntityRefBadge";
import {
  renderDecisionBadge,
  renderSongTypeBadge,
  renderStateBadge,
  renderJobStatusBadge,
  deriveJobStatus,
  formatDateTime,
} from "@/features/processing/processingDisplay";
import { CheckpointTimeline } from "@/features/processing/CheckpointTimeline";
import { PROCESSING_API_UNAVAILABLE_MESSAGE } from "@/features/processing/processingMessages";
import { useUploaderDirectory } from "@/features/processing/uploaderDisplay";
import { JobRef } from "@/features/processing/JobRefBadge";
import {
  isGovernedJob,
  livePlaybackDisplay,
  historicalCheckpointDisplay,
} from "@/features/processing/playbackIntegrityDisplay";
import styles from "./processing-job-detail.module.scss";

const PAGE_ID = "uploader";

function field(label: string, value: React.ReactNode): React.ReactElement {
  return (
    <div className={styles.profileField}>
      <label>{label}</label>
      <div>{value}</div>
    </div>
  );
}

function lifecycleErrorMessage(err: unknown): string {
  const reasonCode = extractProcessingErrorReasonCode(err);
  const message = extractProcessingErrorMessage(err);
  return reasonCode && reasonCode !== "source-intake-missing"
    ? `${message} (${reasonCode})`
    : message;
}

const ProcessingJobDetail: React.FC = () => {
  const { jobId = "" } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const canView = useCanViewPage(PAGE_ID);
  // Write gates — reprocess/resume need uploader edit (D3).
  const canEdit = useCanEditPage(PAGE_ID);

  // PJL-3.1 — human names for actors (owner UX 2026-07-18). The raw Auth0
  // subject stays visible below as an explicit debug field.
  const uploaderDirectory = useUploaderDirectory();

  // Live polling: while the pipeline is moving the job on its own, refresh
  // every 5s so operators watch progress without reloading. Polling stops
  // automatically once the job is terminal (approved / not_approved / failed /
  // upload_expired) or parked (awaiting_review).
  const [jobInFlight, setJobInFlight] = useState(false);
  const { data: job, isLoading, isFetching, isError, error, refetch } =
    useGetProcessingJobQuery(
      { jobId },
      {
        skip: !canView || !jobId,
        pollingInterval: jobInFlight ? 5000 : 0,
      },
    );

  useEffect(() => {
    setJobInFlight(!!job && IN_FLIGHT_JOB_STATES.has(job.state));
  }, [job]);

  const [reprocessJob, reprocessState] = useReprocessProcessingJobMutation();
  const [replaceSongAudio] = useReplaceSongAudioMutation();
  const [resumeJob, resumeState] = useResumeProcessingJobMutation();
  const [archiveJob, archiveState] = useArchiveProcessingJobMutation();
  const [restoreJob, restoreState] = useRestoreProcessingJobMutation();

  const [reprocessOpen, setReprocessOpen] = useState(false);
  const [reprocessError, setReprocessError] = useState("");
  const [reprocessErrorReason, setReprocessErrorReason] = useState("");
  const [resumeOpen, setResumeOpen] = useState(false);
  const [resumeError, setResumeError] = useState("");
  const [confidenceOpen, setConfidenceOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveError, setArchiveError] = useState("");
  // Operator Guidance (owner 2026-07-19) — Technical Details starts collapsed:
  // the detail page is an operational page first, not a diagnostics dump.
  const [techOpen, setTechOpen] = useState(false);
  const replaceFileInputRef = useRef<HTMLInputElement | null>(null);
  const [replaceTarget, setReplaceTarget] = useState<ReplaceSongAudioTarget | null>(
    null,
  );
  const [replaceError, setReplaceError] = useState("");
  const [replaceErrorOpenPath, setReplaceErrorOpenPath] = useState<string | null>(
    null,
  );
  const [replaceBusy, setReplaceBusy] = useState(false);

  const notConfigured = isError && isProcessingApiNotConfigured(error);
  const hardError = isError && !notConfigured;

  const trackId = job?.trackId;
  const {
    data: artifactData,
    isFetching: artifactsFetching,
    isError: artifactsError,
    error: artifactsErr,
    refetch: refetchArtifacts,
  } = useListApprovedArtifactVersionsQuery(
    { trackId: trackId || "" },
    {
      skip: !canView || !trackId,
      // Poll the lineage alongside the job so new versions appear live.
      pollingInterval: jobInFlight ? 5000 : 0,
    },
  );

  // When the job settles (in-flight → terminal/parked), fetch the lineage one
  // last time so the final version chain (e.g. finalize's new AAV) is current
  // even though polling has stopped.
  const wasInFlight = useRef(false);
  useEffect(() => {
    if (wasInFlight.current && !jobInFlight && trackId) {
      refetchArtifacts();
    }
    wasInFlight.current = jobInFlight;
  }, [jobInFlight, trackId, refetchArtifacts]);

  const jobLabel =
    job?.titleHint ||
    job?.intake?.titleHint ||
    job?.originalFilename ||
    job?.intake?.originalFilename ||
    jobId;

  // Operator Guidance — ONE situation drives the badge, the guidance panel,
  // and which actions are offered. Re-derived on every poll/render (stall
  // detection is time-based).
  const jobStatus = job ? deriveJobStatus(job) : null;

  const canReprocess = !!job && REPROCESSABLE_STATES.has(job.state);
  const canResume = job?.state === "awaiting_review";
  const canViewConfidence = jobHasConfidenceAudit(job);
  const isArchived = job?.archived === true;
  const governed = isGovernedJob(job);
  const isHistoryJob =
    governed && job?.isCurrentPlaybackJob === false && !isArchived;
  const canReplaceSongAudio =
    canEdit &&
    !!job &&
    !isArchived &&
    job.isCurrentPlaybackJob === true &&
    !!job.trackId &&
    job.linkedSong?.songStatus === "active";
  // Stalled in-flight jobs are archivable — the API terminalizes them with the
  // dedicated stalled-archived-by-operator reason (their sanctioned escape path).
  // Governed jobs never Archive from Song Processing (Song Library owns lifecycle).
  const canArchive =
    !!job &&
    !isArchived &&
    !governed &&
    (ARCHIVABLE_STATES.has(job.state) || jobStatus === "stalled");
  const liveStatus = job ? livePlaybackDisplay(job) : null;

  const openReplaceFilePicker = () => {
    setReplaceError("");
    setReplaceErrorOpenPath(null);
    const input = replaceFileInputRef.current;
    if (input) {
      input.value = "";
      input.click();
    }
  };

  const onReplaceFilePicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !job?.trackId) return;
    const validationError = validateMp3File(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    const currentArtifact =
      artifactData?.artifactVersions?.find((a) => a.status === "active") || null;
    setReplaceTarget({
      trackId: job.trackId,
      title:
        job.linkedSong?.title ||
        job.titleHint ||
        job.originalFilename ||
        job.trackId,
      primaryArtistName: job.discoveredArtist || job.artistNameHint || null,
      primaryArtistId: null,
      songType: job.songType || null,
      sourceProcessingJobId: job.jobId,
      playbackVersionNumber: currentArtifact?.version ?? job.attempt ?? null,
      approvedObjectKey: currentArtifact?.approvedObjectKey || null,
      checksumApproved: currentArtifact?.checksumApproved || null,
      file,
    });
  };

  const submitReplaceSongAudio = async (replaceReason: string) => {
    if (!replaceTarget) return;
    setReplaceBusy(true);
    setReplaceError("");
    setReplaceErrorOpenPath(null);
    try {
      const init = await initAndPutReplaceSongAudio({
        replaceSongAudio,
        trackId: replaceTarget.trackId,
        file: replaceTarget.file,
        replaceReason,
        songType: replaceTarget.songType,
      });
      toast.success("Replace Song Audio started — processing the new file");
      setReplaceTarget(null);
      navigate(`/processing-jobs/${encodeURIComponent(init.jobId)}`);
    } catch (err) {
      const conflict = extractReplacementAlreadyInProgress(err);
      if (conflict) {
        setReplaceError(conflict.message);
        setReplaceErrorOpenPath(conflict.openReplacementPath);
      } else {
        setReplaceError(
          extractProcessingErrorMessage(err) || "Replace Song Audio failed",
        );
        setReplaceErrorOpenPath(null);
      }
    } finally {
      setReplaceBusy(false);
    }
  };

  const submitArchive = async (reason: string) => {
    if (!job) return;
    setArchiveError("");
    try {
      await archiveJob({ jobId: job.jobId, ...(reason ? { reason } : {}) }).unwrap();
      toast.success("Job archived — it moved to the Archived tab.");
      setArchiveOpen(false);
    } catch (err) {
      setArchiveError(lifecycleErrorMessage(err));
    }
  };

  const submitRestore = async () => {
    if (!job) return;
    try {
      await restoreJob({ jobId: job.jobId }).unwrap();
      toast.success("Job restored to the Active list.");
    } catch (err) {
      toast.error(lifecycleErrorMessage(err) || "Failed to restore job.");
    }
  };

  const submitReprocess = async () => {
    if (!job) return;
    setReprocessError("");
    setReprocessErrorReason("");
    try {
      const result = await reprocessJob({ jobId: job.jobId }).unwrap();
      toast.success(
        `Reprocess started — new job created (v${result.attempt}). Same Track, new version.`,
      );
      setReprocessOpen(false);
      navigate(`/processing-jobs/${encodeURIComponent(result.jobId)}`);
    } catch (err) {
      setReprocessError(lifecycleErrorMessage(err));
      setReprocessErrorReason(extractProcessingErrorReasonCode(err));
    }
  };

  const submitResume = async (corrections: ResumeJobRequest) => {
    if (!job) return;
    setResumeError("");
    try {
      const result = await resumeJob({ jobId: job.jobId, corrections }).unwrap();
      const point =
        result.parkPoint === "catalog-linking" ? "Catalog Linking" : "Audio Processing";
      toast.success(`Processing resumed at ${point}.`);
      setResumeOpen(false);
    } catch (err) {
      setResumeError(lifecycleErrorMessage(err));
    }
  };

  if (notConfigured) {
    return (
      <Container fluid className="p-4">
        <Button variant="link" className={styles.backLink} onClick={() => navigate("/processing-jobs")}>
          ← Back to Song Processing
        </Button>
        <Alert variant="info" className="mb-0">
          <Alert.Heading style={{ fontSize: 18 }}>Music processing unavailable</Alert.Heading>
          <p className="mb-0">{PROCESSING_API_UNAVAILABLE_MESSAGE}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <PageTitle title="Song Processing Detail" />
      <Button variant="link" className={styles.backLink} onClick={() => navigate("/processing-jobs")}>
        ← Back to Song Processing
      </Button>

      {/* PJL-3 — archived banner (owner #6, 2026-07-17): make it immediately
          obvious why this job disappeared from the normal list. */}
      {job && job.archived === true && (
        <Alert
          variant="warning"
          className="d-flex justify-content-between align-items-center flex-wrap gap-2"
          data-testid="archived-banner"
        >
          <div>
            <div style={{ fontWeight: 700, letterSpacing: 1 }}>ARCHIVED</div>
            <div>
              Archived by{" "}
              <strong>
                {job.archivedBy
                  ? uploaderDirectory.resolve(job.archivedBy).label
                  : "unknown"}
              </strong>
              {job.archivedAt ? <> — {formatDateTime(job.archivedAt)}</> : null}
            </div>
            {/* archiveReason is nullable forever (owner #8) — only show when set. */}
            {job.archiveReason ? <div>Reason: {job.archiveReason}</div> : null}
            <div className="text-muted" style={{ fontSize: 13 }}>
              This job is hidden from the Active Song Processing list. History
              and evidence are fully retained.
            </div>
          </div>
          {canEdit && (
            <Button
              variant="outline-primary"
              size="sm"
              disabled={restoreState.isLoading}
              onClick={() => void submitRestore()}
            >
              {restoreState.isLoading ? "Restoring…" : "Restore"}
            </Button>
          )}
        </Alert>
      )}

      {isHistoryJob && (
        <Alert variant="info" data-testid="history-banner" className="mb-3">
          <div style={{ fontWeight: 700, letterSpacing: 1 }}>HISTORY</div>
          <div>
            This is a prior Song Processing attempt for a living Song — not the
            current Details job. It is retained for audit and is never live Ready
            for Playback.
          </div>
          {job.currentProcessingJobId ? (
            <div style={{ marginTop: 8 }}>
              <Link
                to={`/processing-jobs/${encodeURIComponent(job.currentProcessingJobId)}`}
              >
                Open current Song Processing Details
              </Link>
            </div>
          ) : null}
          {job.lineageTrackId || job.trackId ? (
            <div style={{ marginTop: 4 }}>
              <Link
                to={`/processing-jobs?lineageTrackId=${encodeURIComponent(
                  job.lineageTrackId || job.trackId,
                )}`}
              >
                View related Song Processing for this Track
              </Link>
            </div>
          ) : null}
        </Alert>
      )}

      {/* Operator Guidance (owner 2026-07-19) — every situation answers three
          questions up front: what is happening, why, and what to do next. */}
      {job && jobStatus && (
        <JobGuidancePanel
          job={job}
          status={jobStatus}
          canEdit={canEdit}
          canReprocess={canReprocess}
          busy={
            archiveState.isLoading || resumeState.isLoading || reprocessState.isLoading
          }
          onResumeReview={() => {
            setResumeError("");
            setResumeOpen(true);
          }}
          onViewConfidence={
            canViewConfidence ? () => setConfidenceOpen(true) : undefined
          }
          onArchive={
            canArchive
              ? () => {
                  setArchiveError("");
                  setArchiveOpen(true);
                }
              : undefined
          }
          onReprocess={() => {
            setReprocessError("");
            setReprocessErrorReason("");
            setReprocessOpen(true);
          }}
        />
      )}

      <Card>
        <Card.Body>
          {/* Spin only on the initial load — background polls must not flash
              the overlay every 5 seconds. */}
          <Spin spinning={isLoading || (isFetching && !job)}>
            {hardError ? (
              <Alert variant="danger" className="mb-0" data-testid="song-processing-detail-load-error">
                {(() => {
                  const failure = formatSongProcessingLoadFailure(
                    error,
                    extractProcessingErrorMessage,
                  );
                  return (
                    <>
                      <Alert.Heading style={{ fontSize: 18 }}>
                        {failure.title}
                      </Alert.Heading>
                      <p className="mb-2">{failure.body}</p>
                      {failure.technicalRequestId ? (
                        <p className="small text-muted mb-2">
                          Technical details: request ID{" "}
                          <code>{failure.technicalRequestId}</code>
                        </p>
                      ) : null}
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => {
                          void refetch();
                        }}
                        disabled={isFetching}
                      >
                        {isFetching ? "Retrying…" : "Retry"}
                      </Button>
                    </>
                  );
                })()}
              </Alert>
            ) : job ? (
              <>
                <div className={styles.headerRow}>
                  <div>
                    {/* §2.3: originalFilename stands in for Song Title until P3 metadata. */}
                    <h2 className={styles.cardTitle}>
                      {job.titleHint ||
                        job.intake?.titleHint ||
                        job.originalFilename ||
                        job.intake?.originalFilename ||
                        "Song Processing Job"}
                    </h2>
                    {/* Human ref up top; the full canonical ID lives in
                        Technical Details and behind the hover/copy here. */}
                    <div className={styles.jobIdMono}>
                      <JobRef jobId={job.jobId} prefix="Song Processing" />
                    </div>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                    {renderSongTypeBadge(job.songType)}
                    {/* Situation badge — same model as the list Status column
                        (Processing / Waiting for Review / Waiting for Catalog
                        Records / Stalled / terminal outcomes). The raw pipeline
                        state lives in Technical Details. */}
                    {renderJobStatusBadge(job)}
                    {(() => {
                      const purposeLabel = processingJobPurposeLabel(job);
                      return purposeLabel ? (
                        <span className="text-muted" style={{ fontSize: 13 }}>
                          {purposeLabel}
                        </span>
                      ) : null;
                    })()}
                    {canViewConfidence && (
                      <Button
                        variant="outline-dark"
                        size="sm"
                        title="Inspect the confidence score, thresholds, providers, final metadata, and correction history"
                        onClick={() => setConfidenceOpen(true)}
                        data-testid="view-confidence"
                      >
                        Decision Audit
                      </Button>
                    )}
                    {canResume && (
                      <Button
                        variant="warning"
                        size="sm"
                        disabled={!canEdit}
                        title={
                          canEdit
                            ? "Resume this parked job after fixing the review cause"
                            : "You have view-only access to the uploader"
                        }
                        onClick={() => {
                          setResumeError("");
                          setResumeOpen(true);
                        }}
                      >
                        Resume Review
                      </Button>
                    )}
                    {/* Never a bare greyed-out Reprocess (owner 2026-07-19):
                        show it only when usable; otherwise an inline
                        explanation below the header says why not. */}
                    {canReprocess && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={!canEdit}
                        title={
                          canEdit
                            ? "Create the next artifact version from the retained source file"
                            : "You have view-only access to the uploader"
                        }
                        onClick={() => {
                          setReprocessError("");
                          setReprocessErrorReason("");
                          setReprocessOpen(true);
                        }}
                      >
                        Reprocess
                      </Button>
                    )}
                    {canReplaceSongAudio && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        title="Upload new audio bytes for this existing Song"
                        onClick={openReplaceFilePicker}
                        data-testid="job-detail-replace-song-audio"
                      >
                        Replace Song Audio
                      </Button>
                    )}
                    {governed && job.linkedSong?.songLibraryPath ? (
                      <Link
                        to={job.linkedSong.songLibraryPath}
                        className="btn btn-outline-dark btn-sm"
                        title="Open the linked Song in Song Library"
                      >
                        Open Song
                      </Link>
                    ) : null}
                    {governed && (job.lineageTrackId || job.trackId) ? (
                      <Link
                        to={`/processing-jobs?lineageTrackId=${encodeURIComponent(
                          job.lineageTrackId || job.trackId,
                        )}`}
                        className="btn btn-outline-dark btn-sm"
                        title="View related Song Processing attempts for this Track on Active"
                      >
                        View Related Jobs
                      </Link>
                    ) : null}
                    {/* PJL-3 — Archive from the Active lifecycle only; archived
                        jobs show the banner's Restore instead. Governed jobs
                        never Archive here (Song Library owns lifecycle). */}
                    {canArchive && (
                      <Button
                        variant="outline-dark"
                        size="sm"
                        disabled={!canEdit}
                        title={
                          canEdit
                            ? "Remove from the Active list (reversible; history kept)"
                            : "You have view-only access to the uploader"
                        }
                        onClick={() => {
                          setArchiveError("");
                          setArchiveOpen(true);
                        }}
                      >
                        <i className="bi bi-archive" aria-hidden="true" /> Archive
                      </Button>
                    )}
                  </div>
                </div>

                {/* Never a bare disabled Reprocess button (owner 2026-07-19) —
                    when it isn't offered, say exactly why and when it appears. */}
                {!canReprocess && !isArchived && (
                  <p className="text-muted mb-0" style={{ fontSize: 12.5, marginTop: 6 }} data-testid="reprocess-explainer">
                    Reprocess becomes available after this Song Processing job
                    reaches a terminal outcome (Approved or Failed).
                  </p>
                )}

                {/* Operational fields only — internal identifiers, checksums,
                    and debugging fields live in the collapsed Technical
                    Details section below (owner 2026-07-19). */}
                <div className={styles.profileGrid}>
                  {field(
                    "Live Status",
                    liveStatus ? (
                      <div>
                        <div>{liveStatus.label}</div>
                        {liveStatus.summary ? (
                          <div className={styles.muted} style={{ fontSize: 12 }}>
                            {liveStatus.summary}
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      "—"
                    ),
                  )}
                  {field(
                    "Historical Checkpoint",
                    historicalCheckpointDisplay(job),
                  )}
                  {field("Original Filename", job.originalFilename || job.intake?.originalFilename || "—")}
                  {field("Title (upload hint)", job.titleHint || job.intake?.titleHint || "—")}
                  {field("Artist (upload hint)", job.artistNameHint || job.intake?.artistNameHint || "—")}
                  {job.jobPurpose === "replace-song-audio"
                    ? field("Replace reason", job.replaceReason || "—")
                    : null}
                  {field(
                    "Uploader",
                    uploaderDirectory.resolve(job.actorSub, job.actorEmployeeId).label,
                  )}
                  {field("Created", formatDateTime(job.createdAt))}
                  {field("Updated", formatDateTime(job.updatedAt))}
                  {field("Upload Completed", formatDateTime(job.uploadCompletedAt))}
                  {field("Upload Expires", formatDateTime(job.uploadExpiresAt))}
                </div>
              </>
            ) : (
              !isLoading && <Alert variant="info" className="mb-0">Song Processing job not found.</Alert>
            )}
          </Spin>
        </Card.Body>
      </Card>

      {job && !hardError && (
        <>
          <Card className="mt-4">
            <Card.Body>
              <h3 className={styles.sectionTitle}>Checkpoint Timeline</h3>
              <p className={`text-muted ${styles.sectionHelp}`}>
                Every lifecycle checkpoint from upload to playback readiness, in pipeline
                execution order. Expand any step for measured values, thresholds, and
                evidence. Policy Checks remain an explicit development bypass until the
                governance engine ships.
                {jobInFlight
                  ? " Processing is underway — this page refreshes automatically every 5 seconds."
                  : ""}
              </p>
              <CheckpointTimeline
                job={job}
                uploaderLabel={
                  uploaderDirectory.resolve(job.actorSub, job.actorEmployeeId).label
                }
                onOpenDecisionAudit={
                  canViewConfidence ? () => setConfidenceOpen(true) : undefined
                }
              />
            </Card.Body>
          </Card>

          <Card className="mt-4">
            <Card.Body>
              <h3 className={styles.sectionTitle}>Decision</h3>
              <div className={styles.profileGrid}>
                {field("Decision", renderDecisionBadge(job.decision))}
                {/* Metadata-stage parks never write the top-level reasonCode
                    (pre-existing P3c behavior) — fall back to the CMI-3
                    Metadata Identification decision, then the checkpoint. */}
                {field("Reason Code", deriveParkReasonCode(job) || "—")}
                {field("Failed Checkpoint", job.failedCheckpoint ? CHECKPOINT_LABELS[job.failedCheckpoint] ?? job.failedCheckpoint : "—")}
              </div>
            </Card.Body>
          </Card>

          {trackId ? (
            <Card className="mt-4">
              <Card.Body>
                <h3 className={styles.sectionTitle}>Song Play History</h3>
                <p className={`text-muted ${styles.sectionHelp}`}>
                  One current Song Audio (KrossKast Playback MP3) per Song.
                  Replace Song Audio / Reprocess generates a replacement, validates
                  it, cuts over the playback pointer, then removes the superseded
                  MP3. Prior events stay as audit history — not additional playable
                  copies.
                </p>
                <Spin spinning={artifactsFetching && !artifactData}>
                  {artifactsError ? (
                    <Alert variant="danger" className="mb-0">
                      {extractProcessingErrorMessage(artifactsErr) ||
                        "Failed to load approved artifact versions."}
                    </Alert>
                  ) : (artifactData?.artifactVersions?.length ?? 0) === 0 ? (
                    <Alert variant="light" className="mb-0">
                      No approved artifact versions yet (populated at P4 finalize).
                    </Alert>
                  ) : (
                    <LineagePanel
                      trackId={trackId}
                      artifactVersions={artifactData?.artifactVersions ?? []}
                      currentJobId={job.jobId}
                    />
                  )}
                </Spin>
              </Card.Body>
            </Card>
          ) : null}

          <Card className="mt-4">
            <Card.Body>
              <h3 className={styles.sectionTitle}>Governance</h3>
              <p className={`text-muted ${styles.sectionHelp}`}>
                Song Library owns song lifecycle. Song Processing is the audit
                trail for processing attempts.
              </p>
              <div className={styles.profileGrid}>
                {field(
                  "Lifecycle Owner",
                  governed ? "Song Library (governed)" : "Standalone",
                )}
                {field(
                  "Current Details Job",
                  job.isCurrentPlaybackJob === true
                    ? "Yes — this is Track’s current Song Processing Details"
                    : job.isCurrentPlaybackJob === false && governed
                      ? "No — prior attempt (History)"
                      : "—",
                )}
                {field(
                  "Song Status",
                  (() => {
                    const status = job.linkedSong?.songStatus;
                    if (!status || status === "none") {
                      return <span className={styles.muted}>None</span>;
                    }
                    if (status === "active") return "Active";
                    if (status === "archived") return "Archived";
                    if (status === "deleted") return "Deleted";
                    if (status === "missing") return "Missing (orphan Track ID)";
                    return status;
                  })(),
                )}
                {field(
                  "Song Title",
                  job.linkedSong?.title || (
                    <span className={styles.muted}>—</span>
                  ),
                )}
                {field(
                  "Song Library",
                  job.linkedSong?.songLibraryPath ? (
                    <Link to={job.linkedSong.songLibraryPath}>
                      {job.linkedSong.songLibraryLabel || "Open Song"}
                    </Link>
                  ) : (
                    <Link to="/songs">Song Library</Link>
                  ),
                )}
                {field(
                  "Track",
                  trackId ? (
                    <EntityRefBadge kind="track" id={trackId} />
                  ) : (
                    <span className={styles.muted}>
                      Not linked yet (set at P4 track-finalized)
                    </span>
                  ),
                )}
                {field(
                  "Lineage Track",
                  job.lineageTrackId ? (
                    <EntityRefBadge kind="track" id={job.lineageTrackId} />
                  ) : (
                    <span className={styles.muted}>—</span>
                  ),
                )}
              </div>
            </Card.Body>
          </Card>

          {/* Operator Guidance (owner 2026-07-19) — the detail page is an
              operational page first. Internal identifiers, checksums, and
              debugging fields are here, collapsed by default. */}
          <Card className="mt-4">
            <Card.Body>
              <button
                type="button"
                onClick={() => setTechOpen((v) => !v)}
                aria-expanded={techOpen}
                data-testid="tech-details-toggle"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                  width: "100%",
                  textAlign: "left",
                }}
              >
                <i
                  className={`bi ${techOpen ? "bi-chevron-down" : "bi-chevron-right"}`}
                  aria-hidden="true"
                />
                <h3 className={styles.sectionTitle} style={{ marginBottom: 0 }}>
                  Technical Details
                </h3>
                <span className="text-muted" style={{ fontSize: 12.5 }}>
                  internal identifiers &amp; debugging fields
                </span>
              </button>
              {techOpen && (
                <div className={styles.profileGrid} style={{ marginTop: 16 }} data-testid="tech-details-body">
                  {field(
                    "Song Processing ID (canonical)",
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span className={styles.mono}>{job.jobId}</span>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        data-testid="copy-canonical-job-id"
                        onClick={() => {
                          navigator.clipboard
                            .writeText(job.jobId)
                            .then(() => toast.success("Canonical Song Processing ID copied"))
                            .catch(() => toast.error("Could not copy — clipboard unavailable"));
                        }}
                      >
                        Copy canonical ID
                      </Button>
                    </span>,
                  )}
                  {field(
                    "Track ID (canonical)",
                    trackId ? (
                      <span className={styles.mono}>{trackId}</span>
                    ) : (
                      <span className={styles.muted}>—</span>
                    ),
                  )}
                  {field(
                    "Lineage Track ID (canonical)",
                    job.lineageTrackId ? (
                      <span className={styles.mono}>{job.lineageTrackId}</span>
                    ) : (
                      <span className={styles.muted}>—</span>
                    ),
                  )}
                  {field("Pipeline State (raw)", renderStateBadge(job.state))}
                  {field("Upload Source", job.uploadSource || "—")}
                  {field("Uploader (Auth0 subject)", <span className={styles.mono}>{job.actorSub || "—"}</span>)}
                  {field("Uploader (employee)", job.actorEmployeeId || "—")}
                  {field("Master Account", job.actorMasterAccountId || "—")}
                  {field("Correlation ID", <span className={styles.mono}>{job.correlationId || "—"}</span>)}
                  {field(
                    "Checksum Status",
                    job.intakeChecksumStatus || job.intake?.checksumStatus || "—",
                  )}
                  {field(
                    "Checksum (intake)",
                    <span className={styles.mono}>
                      {job.intakeChecksumIntake || job.intake?.checksumIntake || "—"}
                    </span>,
                  )}
                  {field(
                    "Upload Duration (ms)",
                    job.uploadDurationMs != null ? String(job.uploadDurationMs) : "—",
                  )}
                  {field("Attempt", job.attempt ?? "—")}
                  {field("Reprocess Of Job", job.reprocessOfJobId ? <span className={styles.mono}>{job.reprocessOfJobId}</span> : <span className={styles.muted}>—</span>)}
                  {field("Version", job.version ?? "—")}
                </div>
              )}
            </Card.Body>
          </Card>
        </>
      )}

      <ReprocessConfirmModal
        show={reprocessOpen}
        target={
          job
            ? {
                jobId: job.jobId,
                label: jobLabel,
                attempt: job.attempt || 1,
                trackId: job.trackId || null,
                lineageTrackId: job.lineageTrackId || null,
              }
            : null
        }
        busy={reprocessState.isLoading}
        errorMessage={reprocessError}
        errorReasonCode={reprocessErrorReason}
        onCancel={() => setReprocessOpen(false)}
        onConfirm={() => void submitReprocess()}
      />
      <input
        ref={replaceFileInputRef}
        type="file"
        accept="audio/mpeg,.mp3"
        style={{ display: "none" }}
        onChange={onReplaceFilePicked}
        data-testid="job-detail-replace-file-input"
      />
      <ReplaceSongAudioConfirmModal
        show={replaceTarget != null}
        target={replaceTarget}
        busy={replaceBusy}
        errorMessage={replaceError}
        errorOpenPath={replaceErrorOpenPath}
        onCancel={() => {
          if (replaceBusy) return;
          setReplaceTarget(null);
          setReplaceError("");
          setReplaceErrorOpenPath(null);
        }}
        onConfirm={(reason) => void submitReplaceSongAudio(reason)}
      />
      <ResumeReviewModal
        show={resumeOpen}
        job={job ?? null}
        busy={resumeState.isLoading}
        errorMessage={resumeError}
        onCancel={() => setResumeOpen(false)}
        onConfirm={(corrections) => void submitResume(corrections)}
      />
      <ConfidenceAuditModal
        show={confidenceOpen}
        job={job ?? null}
        onClose={() => setConfidenceOpen(false)}
        onOpenResumeReview={
          canResume
            ? () => {
                setResumeError("");
                setResumeOpen(true);
              }
            : undefined
        }
        resolveActor={(sub) => uploaderDirectory.resolve(sub).label}
      />
      <ArchiveJobsModal
        show={archiveOpen}
        jobIds={job ? [job.jobId] : []}
        awaitingReviewCount={job?.state === "awaiting_review" ? 1 : 0}
        stalledCount={jobStatus === "stalled" ? 1 : 0}
        busy={archiveState.isLoading}
        errorMessage={archiveError}
        onCancel={() => setArchiveOpen(false)}
        onConfirm={(reason) => void submitArchive(reason)}
      />
    </Container>
  );
};

export default withAuthenticationRequired(ProcessingJobDetail);
